import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { formatDateLabel, formatTimeLabel, isHoliday } from './pointagesHelpers';

const PointagesBrutsListCard = ({ loading, groupedList, filteredPointages, totalCount, holidays, totalPages, currentPage, setCurrentPage }) => {
  const { t, i18n } = useTranslation();
  return (
  <Card>
    <CardHeader>
      <CardTitle>{t('pointages.list.title')}</CardTitle>
      <CardDescription>
        {totalCount === filteredPointages.length
          ? t('pointages.list.count', { count: totalCount })
          : t('pointages.list.pageCount', { shown: filteredPointages.length, total: totalCount })}
      </CardDescription>
    </CardHeader>
    <CardContent>
      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-lg border p-6 space-y-3">
              <div className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-28" />
                </div>
              </div>
              <div className="space-y-2 pl-14">
                {Array.from({ length: 3 }).map((_, j) => (
                  <Skeleton key={j} className="h-3 w-full" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : !groupedList.length ? (
        <p className="text-sm text-muted-foreground">{t('pointages.list.empty')}</p>
      ) : (
        <>
          <div className="space-y-4">
            {groupedList.map((group) => (
              <Card key={group.key}>
                <CardContent className="p-6">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-8">
                    <div className="md:w-64">
                      <h3 className="text-lg font-semibold">{group.employee.displayName}</h3>
                      <p className="text-sm text-muted-foreground">
                        {[
                          group.employee.registrationNumber ? `${t('attendance.raw.matricule')} ${group.employee.registrationNumber}` : null,
                          group.employee.badge ? `${t('attendance.raw.badge')} ${group.employee.badge}` : null,
                        ]
                          .filter(Boolean)
                          .join(' • ') || t('pointages.list.unknownId')}
                      </p>
                      {group.employee.departmentName && (
                        <p className="text-xs text-muted-foreground mt-1">{group.employee.departmentName}</p>
                      )}
                    </div>

                    <div className="flex-1 space-y-3">
                      {Object.entries(group.dates)
                        .sort(([a], [b]) => new Date(b) - new Date(a))
                        .map(([dateKey, entries]) => {
                          const holidayInfo = isHoliday(dateKey, holidays);
                          return (
                            <div
                              key={dateKey}
                              className={`rounded-lg border p-3 ${holidayInfo ? 'bg-green-950/20 border-green-900/40' : 'bg-muted/20'}`}
                            >
                              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                                <div className="flex items-center gap-2 min-w-[220px]">
                                  <div className="text-sm font-semibold">
                                    {formatDateLabel(dateKey, i18n.language)} — {t('pointages.list.punchCount', { count: entries.length })}
                                  </div>
                                  {holidayInfo && (
                                    <Badge className="bg-green-600 text-white text-xs" title={holidayInfo.description || t('pointages.list.holiday')}>
                                      🎉 {holidayInfo.name}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-2 flex-1">
                                  {entries.map((entry) => (
                                    <span
                                      key={`${entry.id}-${entry.timestamp}`}
                                      className="inline-flex items-center justify-center rounded-md border bg-background px-3 py-1 text-base font-semibold"
                                    >
                                      {formatTimeLabel(entry.timestamp)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                {t('common.previous')}
              </Button>
              <span className="text-sm text-muted-foreground">{t('common.pageOf', { current: currentPage, total: totalPages })}</span>
              <Button
                variant="outline"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                {t('common.next')}
              </Button>
            </div>
          )}
        </>
      )}
    </CardContent>
  </Card>
  );
};

export default PointagesBrutsListCard;
